/**
 * This file is responsible for rendering the entire application.
 *
 * The component uses server-side rendering (getInitialProps) to fetch the initial props for the application.
 *
 * The component uses the 'next/router' library to access the locale from the URL parameters.
 * The locale is used to initialize the i18n instance.
 *
 * The component uses the 'next-i18next' library to access the translations.
 * These translations are used to set the title and description meta tags for the application.
 *
 * The SnackbarProvider component from 'notistack' is used to provide the Snackbar context to the application.
 * The Snackbar context is used to display snackbars (temporary messages) to the user.
 *
 * The ErrorBoundary component from '~/ui/components/common/ErrorBoundary' is used to catch and handle errors in the application.
 *
 * The CoreContext.Provider component from '@Hotels-at-Home/hah-fe-core/dist/context' is used to provide the Core context to the application.
 * The Core context contains various hooks and utilities that can be used throughout the application.
 *
 * The AppLayout component from '~/ui/layouts/App' is used to render the application layout.
 *
 * If there's a network error, the PageMaintenance component from '~/ui/pages/PageMaintenance' is displayed.
 * If there's an error during server-side rendering or client-side rendering, it is passed as a prop to the ServerError component and can be used to display an error message.
 */

import { ReactElement, useEffect } from 'react';
import { AppProps, AppInitialProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { SnackbarProvider } from 'notistack';
import { CoreContext } from '@Hotels-at-Home/hah-fe-core/dist/context';
import { BonvoyAuthProvider } from '@Hotels-at-Home/hah-fe-core/dist/context/auth';
import { checkIE, checkSafari } from '@Hotels-at-Home/hah-fe-core/dist/utils/checkBrowser';
// Import a global CorePackage styles (e.g. for the Swiper carousel)
import '@Hotels-at-Home/hah-fe-core/dist/core.css';

import nextI18NextConfig from '../../next-i18next.config';
import * as thunks from '~/store/thunks';
import * as actions from '~/store/actions';

import { wrapper } from '../store';
import { useSelector, useDispatch, useGetLoadingStatus } from '~/store/hooks';
import { useClientRequest } from '~/utils/app';
import { IAppProps } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { getI18nInstance } from '~/utils/translation';
import { getCartSessionId } from '@Hotels-at-Home/hah-fe-core/dist/utils/cart';
import { serverRequest } from '@Hotels-at-Home/hah-fe-core/dist/utils/app';
import * as SalesforceUtils from '@Hotels-at-Home/hah-fe-core/dist/utils/salesforce';
import AppLayout from '~/ui/layouts/App';
import {
  ErrorBoundary,
  PageMaintenance,
  ServerError,
} from '@Hotels-at-Home/hah-fe-core/dist/components';

import Upgrade from '~/ui/layouts/Upgrade';
import * as siteConfig from '~/constants/siteConfig';
import apolloConfig from '~/constants/apolloConfig';

import '~/ui/assets/styles/app.scss';
import 'react-loading-skeleton/dist/skeleton.css';
import { getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import {
  getCurrencyCode,
  isValidCurrencyCode,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/currency';
import {
  COOKIE_CURRENCY_NAME,
  COOKIE_CURRENCY_PREF,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';
import Script from 'next/script';

const MainHead = (): ReactElement => (
  <Head>
    <meta name="viewport" content="width=device-width, initial-scale=1" key="viewport" />
    <title>{process.env.NEXT_PUBLIC_TITLE}</title>
    <meta charSet="utf-8" key="charset" />
    <meta name="description" content="HotelsAtHome" />
    <link rel="manifest" href="/manifest.json" />
  </Head>
);

const App = ({
  Component,
  pageProps,
  navMenu,
  footer,
  promotion,
  userAgent,
  currency,
  error: serverError,
}: AppProps & IAppProps): ReactElement => {
  const { error: clientError } = useClientRequest();
  const isIE = checkIE(userAgent);
  const isSafari = checkSafari(userAgent);

  const { locale, query, replace } = useRouter();
  const cartSessionId = getCartSessionId();
  const i18n = getI18nInstance(locale);

  const errorMessage = serverError.message || clientError.message;
  const networkErrorStatusCode = serverError.statusCode;

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const contextValue = {
    data: { cartSessionId, locale, currency, siteConfig, promotion },
    useTranslation,
    useSelector,
    useDispatch,
    useGetLoadingStatus,
    apolloConfig,
    thunks,
    actions,
    i18n,
  };

  useEffect(() => {
    const url = new URL(window?.location.href);
    if (url.searchParams.has('setCurrency')) {
      url.searchParams.delete('setCurrency');
    }
    replace(url.href, undefined, { shallow: true });
  }, [query.setCurrency]);

  if (isIE) {
    return (
      <>
        <MainHead />
        <Upgrade />
      </>
    );
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" key="viewport" />
        <title>{process.env.NEXT_PUBLIC_TITLE}</title>
        <meta charSet="utf-8" key="charset" />
        <meta name="description" content="HotelsAtHome" />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="preload"
          href="/fonts/3AC5F6_0_0.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <Script
          dangerouslySetInnerHTML={{
            __html: `
            try {Typekit?.load({ async: true })} catch (e) {console.warn('typekit not loaded')}
            `,
          }}
        />
        <link rel="stylesheet" href="https://use.typekit.net/roi6tcs.css" />
      </Head>
      <Script
        src={`https://${process.env.NEXT_PUBLIC_SF_MID}.collect.igodigital.com/collect.js`}
        onLoad={() => {
          window.addEventListener('CookiebotOnLoad', function () {
            const statisticsConsent = window.Cookiebot.consent.statistics;
            if (statisticsConsent) SalesforceUtils.setTrack(siteConfig.catalogId);
            else SalesforceUtils.setDoNotTrack();
          });
        }}
      />
      <Script
        id="ze-snippet"
        src={`https://static.zdassets.com/ekr/snippet.js?key=${siteConfig.zendeskKey}`}
        strategy="lazyOnload"
      />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <CoreContext.Provider value={contextValue}>
          <BonvoyAuthProvider>
            <ErrorBoundary highLevel>
              <AppLayout
                userAgent={userAgent}
                isSafari={isSafari}
                navMenu={navMenu}
                footer={footer}
                promotion={promotion}
                networkErrorStatusCode={networkErrorStatusCode}
              >
                {networkErrorStatusCode ? (
                  <PageMaintenance />
                ) : (
                  <ErrorBoundary>
                    <Component {...pageProps} />
                  </ErrorBoundary>
                )}
                <ServerError error={errorMessage} />
              </AppLayout>
            </ErrorBoundary>
          </BonvoyAuthProvider>
        </CoreContext.Provider>
      </SnackbarProvider>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore:next-line
App.getInitialProps = async ({ ctx }: AppInitialProps) => {
  const localityParam = ctx.query?.locality;
  const currencyParam = ctx.query?.setCurrency;
  const localityHeader = ctx.req?.headers?.locality || ctx.req?.headers?.Locality;
  const desiredLocale = localityParam ? localityParam.toString().toUpperCase() : localityHeader;
  const currency =
    (isValidCurrencyCode(currencyParam) && currencyParam) ||
    getCookie(COOKIE_CURRENCY_NAME, ctx) ||
    getCookie(COOKIE_CURRENCY_PREF, ctx) ||
    getCurrencyCode(desiredLocale);
  const ctxWithSiteName = {
    ...ctx,
    siteName: siteConfig.scandiWebMenuId,
    currency,
  };
  const res = await serverRequest(apolloConfig, ctxWithSiteName);
  return res;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default wrapper.withRedux(appWithTranslation(App, nextI18NextConfig));
